'use client';

import { useAccountInfo } from '@/hooks/query/account';
import { fetchChatroomUrl, useFetchChatroomToken } from '@/hooks/query/use-chatroom';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAppearanceStore } from '@/store/appearance';
import { useStreamingStore } from '@/store/streaming';
import { ViewEnum } from '@/types';
import { GetGUID, TranslateLanguageToDC, getBetFrom } from '@/utils/common-util';
import { UserInfo } from '@nf/types/account';
import { SiteConfigsSettingsData } from '@nf/types/site';
import { useEffect, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { cx } from 'styled-system/css';
import { Styles } from './chatroom.style';

interface ChatroomProps {
	params: {
		streamingSrc: string;
		realSrc: string;
		streamerExclusiveId: number;
		matchId: number;
		parentId: number;
		isPin: boolean;
	};
}

export const Chatroom = ({ params }: ChatroomProps) => {
	const [isExpnadChatroom, setIsExpnadChatroom, chatroomUrl, setChatroomUrl] = useStreamingStore(
		state => [state.isExpnadChatroom, state.setIsExpnadChatroom, state.chatroomUrl, state.setChatroomUrl]
	);
	const { user } = useAccountInfo();
	const { SiteConfigsData } = useGetSiteConfigs();
	const view = useAppearanceStore(state => state.view);
	const [showChatroom, setShowChatroom] = useState(false);
	const [isExpnadGift, setIsExpnadGift] = useState(false);
	const currentRef = useRef<HTMLDivElement>(null);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const fetchChatroomTokenData = {
		match: GetMatchInfo(params.matchId, params.parentId, params.realSrc),
		SmartUrl: window.location.origin,
		ApibackendUrl: user?.ApiBackendUrl || '',
		AccessToken: user?.AccessToken || '',
		VenderMemberId: user?.VenderMemberId || ''
	}
	const { refetch: refetchFetchChatroomToken } = useFetchChatroomToken(fetchChatroomTokenData);
	useEffect(() => {
		const fetchData = async (matchId: number, parentId: number, streamingSrc: string, streamerExclusiveId: number, SiteConfigsData: SiteConfigsSettingsData, user: UserInfo) => {
			const chatroomToken = await refetchFetchChatroomToken();
			if (!!chatroomToken?.data?.data?.Data) {
				const pData = {
					Data: {
						Seq: GetGUID(),
						UserInfo: GetUserInfo(user, SiteConfigsData, view),
						MatchInfo: GetMatchInfo(matchId, parentId, streamingSrc),
						IsExclusiveStreamer: streamerExclusiveId === parseInt(SiteConfigsData.GetMessageID),
						Token: chatroomToken?.data?.data?.Data,
						SmartUrl: window.location.origin
					}
				}
				fetchChatroomUrl(JSON.stringify(pData), SiteConfigsData.DiscoverChatroomApiUrl).then((ChatroomUrlData) => {
					if (ChatroomUrlData?.data?.Code == 200) {
						setChatroomUrl(ChatroomUrlData?.data?.Result?.Url || '');
					}
				})
			}
		};
		if (SiteConfigsData && user && params.realSrc != '') {
			window.addEventListener('message', ExpandChatroom);
			fetchData(params.matchId, params.parentId, params.realSrc, params.streamerExclusiveId, SiteConfigsData, user);
			setShowChatroom(SiteConfigsData?.EnableChatroom);
		}
		return () => {
			window.removeEventListener('message', ExpandChatroom);
			setIsExpnadChatroom(false);
		}
	}, [params.realSrc]);

	useEffect(() => {
		const handleResize = (entries: ResizeObserverEntry[]) => {
			const { width } = entries[0].contentRect;
			const iframeContentWindow = iframeRef.current?.contentWindow;
			if (width > 1024 && width < 1250) {
				if (iframeContentWindow) {
					setIsExpnadChatroom(true);
					iframeContentWindow.postMessage({ type: 'ChatRoomExpandIcon', action: { IsDisplay: 0 } }, '*');
					iframeContentWindow.postMessage({ type: 'StreamerChatRoom', action: { expandChat: 1 } }, '*');
				}
			} else {
				if (iframeContentWindow) {
					// setIsExpnadChatroom(false);
					iframeContentWindow.postMessage({ type: 'ChatRoomExpandIcon', action: { IsDisplay: 1 } }, '*');
					// iframeContentWindow.postMessage({ type: 'StreamerChatRoom', action: { expandChat: 0 } }, '*');
				}
			}
		};
		const resizeObserver = new ResizeObserver(handleResize);
		resizeObserver.observe(document.documentElement);
		return () => {
			resizeObserver.disconnect();
			setChatroomUrl('')
		};
	}, []);

	useEffect(() => {
		setIsExpnadChatroom(SiteConfigsData?.EnableDefaultOpenChatroom ?? false);
	}, [SiteConfigsData?.EnableDefaultOpenChatroom]);

	const ExpandChatroom = (e: MessageEvent) => {
		if (e.data.type === 'StreamerChatRoom') {
			if (e.data.action.expandChat === 1) {
				setIsExpnadChatroom(true);
				setIsExpnadGift(false);
			} else {
				if (e.data.action.expandGift === 1) {
					setIsExpnadGift(true);
				} else {
					setIsExpnadGift(false);
					setIsExpnadChatroom(false);
				}
			}
		}
	}
	const handleIframeLoad = () => {
		const { width } = document.documentElement.getBoundingClientRect();
		const iframeContentWindow = iframeRef.current?.contentWindow;
		if (width > 1024 && width < 1250) {
			if (iframeContentWindow) {
				setIsExpnadChatroom(true);
				iframeContentWindow.postMessage({ type: 'ChatRoomExpandIcon', action: { IsDisplay: 0 } }, '*');
				iframeContentWindow.postMessage({ type: 'StreamerChatRoom', action: { expandChat: 1 } }, '*');
			}
		}
	};

	return (
		<div ref={currentRef}>
			{showChatroom && user?.ActStatus === 2 ? (
				<div
					className={cx('chatroom', Styles.chatroom)}
					style={{ top: (currentRef.current?.getBoundingClientRect().top ?? 386) + 'px' }}
					{...(['20', '21', '22', '23', '24', '25', '26', '27', '28', '29'].includes(params.streamingSrc.split('-')[0]) ? { 'data-chatroom-open': isExpnadChatroom ? true : false } : {})}
					{...(['20', '21', '22', '23', '24', '25', '26', '27', '28', '29'].includes(params.streamingSrc.split('-')[0]) ? { 'data-chatroom-gift-open': isExpnadGift ? true : false } : {})}
				>
					<iframe src={chatroomUrl}
						ref={iframeRef}
						onLoad={handleIframeLoad}
					>
					</iframe>
				</div>
			) : null}
		</div>
	);
};
const GetMatchInfo = (matchId: number, parentId: number, streamingSrc: string) => {
	return {
		RealMatchId: matchId,
		ParentId: parentId,
		StreamingSrc: streamingSrc + '-' + GetSrcName(streamingSrc)
	}
}


const GetSrcName = (currSrc: string) => {
	const srcMapping: Record<string, string> = {
		'20': '(CN)',
		'21': '(VN)',
		'22': '(TH)',
		'23': '(ID)',
		'24': '(CN)',
		'25': '(VN)',
		'26': '(TH)',
		'27': '(ID)',
		'28': '(PH)',
		'29': '(PH)',
	};

	if (srcMapping[currSrc]) {
		return srcMapping[currSrc];
	} else {
		return '';
	}
};

const GetUserInfo = (user: UserInfo, siteConfigsData: SiteConfigsSettingsData, view: ViewEnum) => {
	const lang = TranslateLanguageToDC(user.Lang || 'en-US');
	return {
		SiteId: siteConfigsData.GetMessageID,
		SiteName: siteConfigsData.SiteName,
		SubsidiaryName: user.SubsidiaryOpratorId,
		CustId: user.ID,
		LicenseeUserName: user.DisplayUserName,
		CurrencyId: user.CurrId,
		CurrencyName: user.Curr,
		BetFrom: getBetFrom(view, isDesktop),
		EnableSingleWallet: user.EnableSingleWallet,
		UserLanguage: lang.toUpperCase(),
		TsId: user.TsId,
		BaStatus: user.BaStatus,
		EnableDefaultOpenChatroom: siteConfigsData.EnableDefaultOpenChatroom,
		VenderMemberId: user.VenderMemberId
	}
}
